<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { eqsApi } from '@/api/eqs';
import PersonPicker from '@/components/PersonPicker.vue';
import { notify } from '@/services/notice';
import { errorMessage } from '@/utils/format';
import { personLabel, type PickablePerson } from '@/utils/person';
import { confirmAction } from '@/services/confirm';

interface Reviewer {
  id: number;
  stepOrder: number;
  userId: number | null;
  reviewerName: string;
  conclusion: 'approved' | 'rejected' | null;
  comment: string | null;
  decidedAt: string | null;
  isExcluded: boolean;
  isMine: boolean;
  actionable: boolean;
}

/** 被作废过的一轮评审（责任人重新提交时清掉的那些，从审计里读回来） */
interface PastRound {
  round: number;
  reopenedAt: string;
  reopenedBy: string;
  reason: string;
  reviewers: Array<{
    reviewerName: string;
    stepOrder: number;
    conclusion: 'approved' | 'rejected';
    comment: string | null;
    decidedAt: string | null;
  }>;
}

const props = defineProps<{
  projectId: string | number;
  nodeId: string | number;
  canWrite?: boolean;
  people?: PickablePerson[];
}>();

const emit = defineEmits<{
  /** 结论落定（approved/rejected）时通知外层刷新交付物状态 */
  settled: [];
  /**
   * 名单读完了：有几行（含作废过的轮次）；没读到是 null。
   * 外层据此决定显不显示这块——0916 停发之后绝大多数节点一行都没有（0930）。
   */
  loaded: [rows: number | null];
}>();

interface ReviewBrief {
  generatedAt: string;
  cached: boolean;
  structural: {
    nodeName: string; deliverableTotal: number; withDocument: number; missingTitles: string[];
    /** 已取消的输出物标题；作废不等于没有，要单独报（0915） */
    voidedTitles?: string[];
  };
  files: Array<{ fileName: string; readable: boolean; note: string; vision?: boolean }>;
  summary: string | null;
  checks: Array<{ item: string; status: 'ok' | 'missing' | 'unclear'; detail: string }>;
  aiNote: string | null;
}

const CHECK_ICON: Readonly<Record<string, string>> = { ok: '✓', missing: '✗', unclear: '?' };

/** 预读读不了的那几个：这个必须说，否则人会以为 AI 全看过了 */
const unreadableFiles = computed(() => (brief.value?.files ?? []).filter((file) => !file.readable));

/*
  看图看出来的那几个（0916）。

  扫描件和图片现在能读了（交给 mimo-v2.5），但它和从文字层抽出来的字
  **分量不一样**：模型可能把 8 看成 3。所以单独标出来，让评审人知道
  下面那段摘要的依据是"模型看图转写的"，该核的数字自己再核一眼。
*/
const visionFiles = computed(() => (brief.value?.files ?? []).filter((file) => file.vision));

const reviewers = ref<Reviewer[]>([]);
const brief = ref<ReviewBrief | null>(null);
const briefLoading = ref(false);

async function loadBrief(refresh = false): Promise<void> {
  briefLoading.value = true;
  try {
    brief.value = await eqsApi.nodeReviewBrief(props.projectId, props.nodeId, refresh) as unknown as ReviewBrief;
  } catch (caught) {
    notify('error', '预读失败', errorMessage(caught));
  } finally {
    briefLoading.value = false;
  }
}
const loading = ref(false);
const submitting = ref(false);
const comment = ref('');
const addUserId = ref<number | null>(null);

const decideBox = ref<HTMLElement | null>(null);

/** 行上的「去出结论」滚到底部的结论框并聚焦意见栏 */
function focusDecide(): void {
  decideBox.value?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  decideBox.value?.querySelector('textarea')?.focus();
}

const myTurn = computed(() => reviewers.value.find((item) => item.actionable) ?? null);
const activeRows = computed(() => reviewers.value.filter((item) => !item.isExcluded));
const pendingCount = computed(() =>
  activeRows.value.filter((item) => item.conclusion === null).length);

/**
 * 评审是**按级顺序**走的，但界面上每个人都写着「待评审」、右边只有一个「移除」，
 * 人打开根本不知道该干什么，也不知道在等谁（试用反馈 8/13 截图）。
 * 下面这几个算的就是「现在轮到谁、我要不要动手」，直接写在面板顶上。
 */
const currentStep = computed<number | null>(() => {
  const pending = activeRows.value.filter((item) => item.conclusion === null);
  if (!pending.length) return null;
  return Math.min(...pending.map((item) => item.stepOrder));
});

/** 当前这一级待出结论的人（同级可能有多个） */
const currentReviewers = computed(() => activeRows.value.filter(
  (item) => item.conclusion === null && item.stepOrder === currentStep.value,
));

/**
 * 还没上传文件的输出物。
 *
 * 后端已经拦住「没证据不许通过」了，这里是把话提前说：
 * 让评审人一打开就看到差什么，而不是写完意见点了通过才吃一个报错。
 */
interface MissingDeliverable { id: number; title: string; ownerName: string | null }
const missingDeliverables = ref<MissingDeliverable[]>([]);

/**
 * 已经交上来的输出物——**评审人要点开看的就是这个**（0904 Tony:
 * 「我去系统里面审批，但我看不到成员上传的交付物，只能看到 AI 预读」）。
 *
 * 它和上面的 AI 预读是两回事，故意分开：
 *   · 预读会缓存、最多只读 3 个文件、AI 挂了就没有；
 *   · 这份清单直接来自交付物记录，永远是全的，AI 跑不跑都在。
 * 把「能不能看到原件」挂在一次 AI 调用上，是本末倒置。
 */
interface SubmittedDeliverable {
  id: number; title: string; ownerName: string | null;
  documentId: number; versionId: number | null;
  fileName: string; sizeBytes: number | null; uploadedAt: string | null;
  /** 已取消的交付物：照样能下载，但不算有效证据（0915） */
  voided?: boolean;
}
const submittedDeliverables = ref<SubmittedDeliverable[]>([]);

/*
  0915 Tony:「这个交付物上传的文件我没找到」。

  真因：节点 2176 的交付物早就是 cancelled，而后端原来在 WHERE 里把作废的
  直接滤掉了，于是这一段空白；可 AI 预读用的是另一条查询（`attachments()`
  不看状态，它还要给打包下载用），照样把那份作废的费用分析表读出来给了
  「预算 176.5 万、13 套模具」的详细结论——**人看得到 AI 的分析，却找不到文件**。
  全库 12 个节点这样，其中 11 个只剩作废的交付物。

  现在两段分开摆：在用的在上面，作废的在下面并标出来。
*/
const liveDeliverables = computed(() => submittedDeliverables.value.filter((item) => !item.voided));
const voidedDeliverables = computed(() => submittedDeliverables.value.filter((item) => item.voided));

function fileHref(item: SubmittedDeliverable): string | null {
  if (item.versionId === null) return null;
  return eqsApi.fileDownloadUrl(item.documentId, item.versionId);
}

function fileSize(bytes: number | null): string {
  if (bytes === null || !Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
const evidenceReady = computed(() => missingDeliverables.value.length === 0);

/*
  把「传文件」放进评审面板：催办通知把人带到这里，缺哪份、点哪份的「上传」，
  传完门禁自动抬——不用再教人先去交付物页找到那一条再回来。
  写权限后端照拦（不在项目组会明说），这里不做前置隐藏。
*/
const mailFilingAddress = ref<string | null>(null);
const previousRounds = ref<PastRound[]>([]);

/*
  历轮默认展开还是收起，看**上一轮有没有人退回**。

  被退回过 → 这一轮该改什么就写在那条意见里，是重评的人第一眼要看的东西，
  展开。只是换了个文件重新提交（上一轮全通过）→ 收起，别把面板撑长。
*/
const lastRound = computed(() => previousRounds.value[previousRounds.value.length - 1] ?? null);
const lastRoundRejections = computed(() =>
  (lastRound.value?.reviewers ?? []).filter((row) => row.conclusion === 'rejected'));
const uploadingId = ref<number | null>(null);
async function uploadFor(item: MissingDeliverable, event: Event): Promise<void> {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = '';
  if (!file || uploadingId.value !== null) return;
  uploadingId.value = item.id;
  try {
    const form = new FormData();
    form.append('file', file);
    await eqsApi.attachDeliverableFile(props.projectId, item.id, form);
    await load();
    notify('success', '文件已上传',
      evidenceReady.value
        ? `「${item.title}」已挂上正文，这个节点的评审可以往下走了。`
        : `「${item.title}」已挂上正文，还差 ${missingDeliverables.value.length} 份。`);
  } catch (caught) {
    notify('error', '上传失败', errorMessage(caught));
  } finally {
    uploadingId.value = null;
  }
}

/** 催办走节点催办的老通道（站内信，一天一条），留言里写清缺哪几份 */
const reminding = ref(false);
async function remindEvidence(): Promise<void> {
  if (reminding.value) return;
  reminding.value = true;
  try {
    const titles = missingDeliverables.value.map((item) => item.title).join('、');
    const outcome = await eqsApi.remindPlanNode(
      props.projectId, props.nodeId,
      `评审在等交付物：「${titles}」还没上传，传上去评审才能通过。`,
    ) as { notified?: number; ownerName?: string | null; skipped?: string | null };
    if (outcome.notified) {
      notify('success', '已催办', `${outcome.ownerName ?? '责任人'}会在系统里收到提醒。`);
    } else {
      notify('error', '没催出去', outcome.skipped ?? '未知原因');
    }
  } catch (caught) {
    notify('error', '催办失败', errorMessage(caught));
  } finally {
    reminding.value = false;
  }
}

const myPendingRow = computed(() => activeRows.value.find(
  (item) => item.isMine && item.conclusion === null,
) ?? null);

const statusLine = computed(() => {
  if (!activeRows.value.length) return '';
  if (currentStep.value === null) return '所有评审人都已出结论。';
  const names = currentReviewers.value.map((item) => item.reviewerName).join('、');
  if (myTurn.value) {
    return `轮到你了（第 ${currentStep.value} 级）。在下面写意见并选择通过或退回。`;
  }
  if (myPendingRow.value) {
    return `评审按级顺序进行，现在等第 ${currentStep.value} 级的 ${names} 出结论；`
      + `你在第 ${myPendingRow.value.stepOrder} 级，轮到你时会收到通知。`;
  }
  return `评审按级顺序进行，现在等第 ${currentStep.value} 级的 ${names} 出结论。`;
});

/** 每一行的状态文案：不能全写「待评审」，看不出在等谁 */
function rowState(reviewer: Reviewer): string {
  if (reviewer.isExcluded) return '已移除';
  if (reviewer.conclusion === 'approved') return '通过';
  if (reviewer.conclusion === 'rejected') return '退回';
  if (reviewer.stepOrder === currentStep.value) {
    return reviewer.actionable ? '轮到你出结论' : '正在评审中';
  }
  return `等第 ${currentStep.value} 级出结论`;
}

async function load(): Promise<void> {
  loading.value = true;
  try {
    const payload = await eqsApi.nodeReviewers(props.projectId, props.nodeId);
    reviewers.value = (payload.reviewers ?? []) as unknown as Reviewer[];
    missingDeliverables.value = (payload.missingDeliverables ?? []) as unknown as MissingDeliverable[];
    submittedDeliverables.value = (
      payload.submittedDeliverables ?? []) as unknown as SubmittedDeliverable[];
    mailFilingAddress.value = (payload.mailFilingAddress as string | null) ?? null;
    previousRounds.value = (payload.previousRounds ?? []) as unknown as PastRound[];
    emit('loaded', reviewers.value.length + previousRounds.value.length);
  } catch {
    // 加载不了评审名单不该拖垮整个抽屉
    reviewers.value = [];
    missingDeliverables.value = [];
    submittedDeliverables.value = [];
    previousRounds.value = [];
    emit('loaded', null);
  } finally {
    loading.value = false;
  }
}

async function decide(conclusion: 'approved' | 'rejected'): Promise<void> {
  const target = myTurn.value;
  if (!target) return;
  if (conclusion === 'rejected' && !comment.value.trim()) {
    notify('error', '无法退回', '退回必须填写评审意见，否则被退的人不知道要改什么。');
    return;
  }
  submitting.value = true;
  try {
    const result = await eqsApi.decideNodeReview(props.projectId, props.nodeId, target.id, {
      conclusion,
      comment: comment.value.trim() || undefined,
    }) as { reviewers: Reviewer[]; outcome: string | null };
    reviewers.value = result.reviewers;
    comment.value = '';
    /*
      提示语先说**你刚做了什么**，再说链条到哪一步了。

      0909 Tony:「点『通过』之后，弹窗显示『已退回责任人』」。原来这里只看
      `outcome`（整条链的结论），不看这个人刚点的是什么——而链条只要有人退回过
      就永远算 rejected（后端 finalize 的 `some(conclusion === 'rejected')`
      看的是所有人所有时候）。于是在「有人退回过」的节点上，谁点通过都会看到
      「已退回，责任人会收到退回通知」，像是自己把它退了。
      线上有 9 个节点是这种「有人退回 + 有人通过」的混合态。
    */
    if (conclusion === 'rejected') {
      notify('success', '已退回', '责任人会收到退回通知。');
      emit('settled');
    } else if (result.outcome === 'approved') {
      notify('success', '评审链已全部通过', '交付物审批状态已更新为已批准。');
      emit('settled');
    } else if (result.outcome === 'rejected') {
      // 自己点的是通过、链条却是退回：一定是这个节点上有人退回过，把是谁说出来
      const blockers = (result.reviewers ?? [])
        .filter((row) => !row.isExcluded && row.conclusion === 'rejected')
        .map((row) => row.reviewerName);
      notify(
        'info',
        '你的通过已记录，但这个节点仍是退回状态',
        `${blockers.join('、') || '之前有评审人'}退回过，所以整条链的结论还是「退回」。`
          + '要让它重新走通，得先由责任人按退回意见改完重新提交。',
      );
      emit('settled');
    } else {
      notify('success', '你的通过已记录', '已通知下一步评审人。');
    }
  } catch (caught) {
    notify('error', '评审提交失败', errorMessage(caught));
  } finally {
    submitting.value = false;
  }
}

function onUnresolved(text: string): void {
  notify('error', '没找到这个人', `系统里没有唯一叫「${text}」的账号。从下拉建议里选一个。`);
}

async function addReviewer(): Promise<void> {
  if (addUserId.value === null) return;
  submitting.value = true;
  try {
    const payload = await eqsApi.addNodeReviewer(props.projectId, props.nodeId, {
      userId: addUserId.value,
    });
    reviewers.value = (payload.reviewers ?? []) as unknown as Reviewer[];
    addUserId.value = null;
    notify('success', '已追加评审人', '这个人的「我的工作」里会出现待评审。');
  } catch (caught) {
    notify('error', '追加评审人失败', errorMessage(caught));
  } finally {
    submitting.value = false;
  }
}

async function exclude(reviewer: Reviewer): Promise<void> {
  if (!await confirmAction({ title: `确认移除评审人“${reviewer.reviewerName}”？移除后不参与链条结算。`, danger: true })) return;
  submitting.value = true;
  try {
    const result = await eqsApi.excludeNodeReviewer(props.projectId, props.nodeId, reviewer.id) as {
      reviewers: Reviewer[]; outcome: string | null;
    };
    reviewers.value = result.reviewers;
    if (result.outcome) emit('settled');
  } catch (caught) {
    notify('error', '移除失败', errorMessage(caught));
  } finally {
    submitting.value = false;
  }
}

function conclusionLabel(reviewer: Reviewer): string {
  if (reviewer.isExcluded) return '已移除';
  if (reviewer.conclusion === 'approved') return '通过';
  if (reviewer.conclusion === 'rejected') return '退回';
  return '待评审';
}

watch(() => [props.projectId, props.nodeId], () => {
  brief.value = null;
  void load();
}, { immediate: true });
</script>

<template>
  <section class="review-panel" aria-label="节点评审">
    <header>
      <b>节点评审（指名到人）</b>
      <small v-if="activeRows.length">
        {{ activeRows.length - pendingCount }} / {{ activeRows.length }} 已出结论
      </small>
    </header>
    <!--
      0914 Tony 第 8 条:「阶段工作与项目交付物的审批流不一致」。

      这两件事本来就是两条链,名字一样才让人以为该一致:
      · **交付物审批**（wf_approval_instances）按审批矩阵逐级走,在「交付物」页;
      · **节点评审**（wf_plan_node_reviewers）是旧 EQS 迁来的指名到人,就是这一块。
      0911 已经把这一页上那张带进度的流转图撤掉了（它按节点挑实例,挑到的常常是
      同节点下别的交付物的进度）。剩下这一块改个名、把关系写在明面上——
      直接删掉的话,1328 条在走的指名评审就没有入口了。
    -->
    <!--
      0916 Tony 拍板：「只走交付物审批，不走节点审批」。

      停的是**发**不是**收**：名单上的人照旧出结论、照旧能被移除（存量要收尾），
      但不再自动铺人、也不再能手工加人。等存量收完，这一块整个撤掉。
      话要说在最前面——不说的话，人只会看到「追加」按钮不见了，以为坏了。
    -->
    <p class="review-scope is-retiring">
      <b>节点评审正在收尾。</b>0916 起签字只走一条链：在「交付物」页提交输出物走审批。
      这里剩下的名单还能出结论、能移除，但不再新增；收完之后这一块会撤掉。
    </p>

    <!-- 打开就告诉人：现在轮到谁、我要不要动手 -->
    <p v-if="statusLine" class="review-status" :class="{ 'is-mine': Boolean(myTurn) }">
      {{ statusLine }}
    </p>

    <!--
      交上来的原件排在 AI 预读**前面**：要看的是文件本身，预读只是帮你决定
      先看哪一份。顺序反过来的话，人一打开看到的是 AI 的话，反而找不到东西在哪。
    -->
    <div v-if="submittedDeliverables.length" class="evidence-box">
      <h4 v-if="liveDeliverables.length">已交的输出物（{{ liveDeliverables.length }}）</h4>
      <ul v-if="liveDeliverables.length" class="evidence-list">
        <li v-for="item in liveDeliverables" :key="item.id">
          <a
            v-if="fileHref(item)" class="evidence-link" :href="fileHref(item)!"
            target="_blank" rel="noopener">{{ item.fileName }}</a>
          <span v-else class="evidence-broken">{{ item.fileName }}（文件本体缺失）</span>
          <small>
            {{ item.title }}<template v-if="item.ownerName"> · {{ item.ownerName }}</template>
            <template v-if="item.uploadedAt"> · {{ item.uploadedAt.slice(0, 10) }}</template>
            <template v-if="fileSize(item.sizeBytes)"> · {{ fileSize(item.sizeBytes) }}</template>
          </small>
        </li>
      </ul>

      <!--
        已取消的单列一段：文件还在、还能下载（人要查那份到底写了什么），
        但写明它不算证据。全滤掉的话就是 0915 那一幕——AI 有分析、人找不到文件。
      -->
      <template v-if="voidedDeliverables.length">
        <h4 class="evidence-voided-title">
          已取消的输出物（{{ voidedDeliverables.length }}）
          <em>不作为评审证据，文件留着备查</em>
        </h4>
        <ul class="evidence-list is-voided">
          <li v-for="item in voidedDeliverables" :key="item.id">
            <a
              v-if="fileHref(item)" class="evidence-link" :href="fileHref(item)!"
              target="_blank" rel="noopener">{{ item.fileName }}</a>
            <span v-else class="evidence-broken">{{ item.fileName }}（文件本体缺失）</span>
            <small>
              {{ item.title }} · 已取消<template v-if="item.ownerName"> · {{ item.ownerName }}</template>
              <template v-if="fileSize(item.sizeBytes)"> · {{ fileSize(item.sizeBytes) }}</template>
            </small>
          </li>
        </ul>
        <p v-if="!liveDeliverables.length" class="evidence-warn">
          这个节点的输出物<b>全部已取消</b>，没有有效证据。上面的 AI 预读也不再读它们——
          要评审得先让责任人重新上传。
        </p>
      </template>
    </div>

    <!-- 预读：先看「这是什么、缺什么」，再决定花不花时间细看 -->
    <div class="brief-box">
      <div class="brief-head">
        <button class="inline-button" type="button" :disabled="briefLoading" @click="loadBrief(false)">
          {{ briefLoading ? '预读中…' : brief ? '重新预读' : 'AI 预读交付物' }}
        </button>
        <small v-if="brief?.cached">
          复用 {{ brief.generatedAt.slice(0, 10) }} 的结果（文件未变）·
          <button class="brief-refresh" type="button" @click="loadBrief(true)">强制重新生成</button>
        </small>
      </div>
      <template v-if="brief">
        <p class="brief-structural">
          输出物 {{ brief.structural.deliverableTotal }} 个，{{ brief.structural.withDocument }} 个已挂文件<template
            v-if="brief.structural.missingTitles.length">；<b>缺：{{ brief.structural.missingTitles.join('、') }}</b></template><template
            v-if="brief.structural.voidedTitles?.length">；另有 {{ brief.structural.voidedTitles.length }} 个已取消（没读）</template>
        </p>
        <!--
          只列**读不了的**。能读的那些上面已经带链接列过一遍了，
          在这儿再来一份点不动的同名清单，只会让人以为那才是全部。
          读不了的必须留着：静默跳过等于让人以为 AI 看过了。
        -->
        <ul v-if="unreadableFiles.length" class="brief-files">
          <li v-for="file in unreadableFiles" :key="file.fileName" class="unreadable">
            {{ file.fileName }} <small>（{{ file.note }}）</small>
          </li>
        </ul>
        <!--
          看图看出来的单独标一行：模型可能把 8 看成 3，
          让评审人知道下面这段摘要的依据是转写，不是原文。
        -->
        <p v-if="visionFiles.length" class="brief-vision">
          <b>以下内容是模型「看图」读出来的</b>，不是从文件里抽的文字，数字请对着原件再核一眼：
          <span v-for="file in visionFiles" :key="file.fileName">{{ file.fileName }}（{{ file.note }}）</span>
        </p>
        <p v-if="brief.summary" class="brief-summary">{{ brief.summary }}</p>
        <ul v-if="brief.checks.length" class="brief-checks">
          <li v-for="check in brief.checks" :key="check.item" :class="check.status">
            <i>{{ CHECK_ICON[check.status] }}</i>
            <span><b>{{ check.item }}</b> — {{ check.detail }}</span>
          </li>
        </ul>
        <p v-if="brief.aiNote" class="brief-note">{{ brief.aiNote }}</p>
        <p class="brief-note">预读只供参考，不代替评审结论；通过 / 退回仍由评审人判断。</p>
      </template>
    </div>

    <p v-if="loading" class="review-hint">正在读取评审名单…</p>
    <p v-else-if="!reviewers.length" class="review-hint">
      本节点没有指名评审人。需要评审的交付物走「需评审」开关的审批流；
      这里的名单是逐人指定的评审（旧 EQS 的评审模式）。
    </p>

    <ol v-else class="review-steps">
      <li
        v-for="reviewer in reviewers"
        :key="reviewer.id"
        :class="{
          excluded: reviewer.isExcluded,
          approved: reviewer.conclusion === 'approved',
          rejected: reviewer.conclusion === 'rejected',
          actionable: reviewer.actionable,
        }"
      >
        <span class="review-step">{{ reviewer.stepOrder }}</span>
        <div class="review-body">
          <b>
            {{ reviewer.reviewerName }}
            <i v-if="reviewer.userId === null" title="没有关联系统账号，本人无法在系统里出结论">无账号</i>
            <i v-if="reviewer.isMine" class="mine">我</i>
          </b>
          <small>
            {{ rowState(reviewer) }}
            <template v-if="reviewer.decidedAt"> · {{ reviewer.decidedAt.slice(0, 10) }}</template>
          </small>
          <p v-if="reviewer.comment" class="review-comment">{{ reviewer.comment }}</p>
        </div>
        <!--
          两个按钮收在同一格里（0828 Tony:「这个字都出框了」）。

          这一行是 `grid-template-columns: 26px 1fr auto` 三列，
          可轮到我的那一行有**四个**子元素（序号 / 正文 / 去出结论 / 移除）——
          第四个落进了隐式的第二行，配上 align-items:start 就跑到卡片外面，
          压在下一个人身上。包一层，网格永远只有三列。
        -->
        <div class="review-acts">
          <!-- 轮到我时按钮就在行上，不用去底部找；「移除」是次要动作，降权显示 -->
          <button
            v-if="reviewer.actionable"
            class="button button-primary review-row-decide"
            type="button"
            @click="focusDecide"
          >去出结论</button>
          <button
            v-if="canWrite && reviewer.conclusion === null && !reviewer.isExcluded"
            class="review-remove"
            type="button"
            :disabled="submitting"
            :title="`把${reviewer.reviewerName}从这个节点的评审名单里去掉（不是评审结论）`"
            :aria-label="`移除评审人${reviewer.reviewerName}`"
            @click="exclude(reviewer)"
          >移除</button>
        </div>
      </li>
    </ol>

    <!--
      被作废过的历轮。位置故意压在「出结论」正上方：重评的人要先看到上一轮
      要求改什么，再写自己的结论。收在「操作留痕」页里等于没有。
    -->
    <details v-if="previousRounds.length" class="review-rounds" :open="lastRoundRejections.length > 0">
      <summary>
        之前有 <b>{{ previousRounds.length }}</b> 轮评审被作废
        <template v-if="lastRoundRejections.length">
          ·
          <em>上一轮被{{ lastRoundRejections.map((row) => row.reviewerName).join('、') }}退回</em>
        </template>
      </summary>
      <div v-for="past in [...previousRounds].reverse()" :key="past.round" class="review-round">
        <p class="round-head">
          第 {{ past.round }} 轮
          <small>{{ past.reopenedAt.slice(0, 10) }} 作废 · {{ past.reason || '责任人重新提交' }}</small>
        </p>
        <ul class="round-list">
          <li v-for="(row, index) in past.reviewers" :key="`${past.round}-${index}`"
              :class="{ rejected: row.conclusion === 'rejected' }">
            <span class="round-who">{{ row.reviewerName }}</span>
            <span class="round-verdict">{{ row.conclusion === 'approved' ? '通过' : '退回' }}</span>
            <span v-if="row.decidedAt" class="round-when">{{ row.decidedAt.slice(0, 10) }}</span>
            <p v-if="row.comment" class="round-comment">{{ row.comment }}</p>
          </li>
        </ul>
      </div>
    </details>

    <div v-if="myTurn" ref="decideBox" class="review-decide">
      <b class="decide-title">你的评审结论（第 {{ myTurn.stepOrder }} 级）</b>
      <textarea
        v-model="comment"
        rows="2"
        maxlength="1000"
        placeholder="评审意见（退回时必填）"
        aria-label="评审意见"
      ></textarea>
      <!-- 没交东西就别让人点通过：后端会拦，这里直接给出「传」和「催」两条出路 -->
      <div v-if="!evidenceReady" class="evidence-block">
        <p>
          <b>还差 {{ missingDeliverables.length }} 份输出物没上传</b>——
          评审要看东西，没有附件的通过等于空签字。是你的就地上传；
          不是你的点催办；确实交不出来的写明理由<b>退回</b>。
        </p>
        <ul class="evidence-list">
          <li v-for="item in missingDeliverables" :key="item.id">
            <span class="evidence-title">{{ item.title }}</span>
            <span class="evidence-owner">{{ item.ownerName ?? '没写责任人' }}</span>
            <code class="evidence-token" :title="'邮件转发入库的编号'">#D{{ item.id }}</code>
            <label class="inline-button evidence-upload" :class="{ busy: uploadingId === item.id }">
              {{ uploadingId === item.id ? '上传中…' : '上传文件' }}
              <input
                type="file" hidden :disabled="uploadingId !== null"
                :aria-label="`为「${item.title}」上传文件`"
                @change="uploadFor(item, $event)"
              >
            </label>
          </li>
        </ul>
        <button class="inline-button" type="button" :disabled="reminding" @click="remindEvidence">
          {{ reminding ? '催办中…' : '催办责任人' }}
        </button>
        <p v-if="mailFilingAddress" class="evidence-mail-hint">
          文件在邮箱里的话不用下载再传：把那封邮件<b>连附件转发到 {{ mailFilingAddress }}</b>，
          主题或正文写上对应的 #D 号（如 #D{{ missingDeliverables[0]?.id }}），系统自动归档并回执。
        </p>
      </div>
      <div class="review-actions">
        <button
          class="button button-primary" type="button"
          :disabled="submitting || !evidenceReady"
          :title="evidenceReady ? undefined : '输出物还没上传，不能通过'"
          @click="decide('approved')"
        >
          {{ submitting ? '提交中…' : '通过' }}
        </button>
        <button class="button review-reject" type="button" :disabled="submitting" @click="decide('rejected')">
          退回
        </button>
      </div>
    </div>

    <!--
      「追加评审人」0916 撤掉（后端 assertNodeReviewIssuable 也拦一道：
      界面藏了不等于拦住，拼一个 POST 照样进得来）。
      要加签字人请去交付物页指定审批人——那条路 0916 刚补上。
    -->
  </section>
</template>

<style scoped>
.review-scope {
  margin: 0 0 8px; color: var(--muted); font-size: 12px; line-height: 1.7;
}
.review-scope b { color: var(--ink); }
.review-scope.is-retiring { border-left: 3px solid var(--amber, #b4761c); padding-left: 9px; background: rgba(180,118,28,.06); }

.evidence-block {
  margin: 0 0 10px; padding: 9px 11px;
  border-left: 3px solid var(--warning-graphic, #e8a13a); border-radius: 3px;
  background: #fdf6ec; color: #7a4a10; font-size: 12px; line-height: 1.7;
}
.evidence-block > p { margin: 0 0 8px; }
.evidence-list { display: grid; gap: 5px; margin: 0 0 8px; padding: 0; list-style: none; }
.evidence-list li { display: flex; align-items: center; gap: 8px; }
.evidence-title { overflow: hidden; color: var(--ink); font-weight: 700; text-overflow: ellipsis; white-space: nowrap; }
.evidence-owner { flex-shrink: 0; color: var(--muted); }
.evidence-upload { margin-left: auto; flex-shrink: 0; cursor: pointer; }
.evidence-token {
  flex-shrink: 0; padding: 0 5px; border: 1px dashed var(--line-strong);
  border-radius: 3px; color: var(--muted); font-size: 11px; user-select: all;
}
.evidence-mail-hint { margin: 8px 0 0; color: var(--muted); font-size: 11px; line-height: 1.7; }
.evidence-mail-hint b { color: var(--ink); }
.evidence-upload.busy { opacity: .55; cursor: wait; }
.review-status {
  margin: 0 0 10px; padding: 9px 12px; border-radius: var(--radius-s);
  background: rgba(0, 117, 174, .06); color: var(--ink); font-size: 12px; line-height: 1.7;
}
.review-status.is-mine { background: rgba(0, 117, 174, .12); font-weight: 700; }
/*
  行末的按钮区：横排、窄了就换行，但**始终在这一格里**，不会跑到卡片外。
  min-width:0 是给中间那格留的——名字长的时候由正文去挤，不是把按钮挤出去。
*/
.review-acts { display: flex; flex-wrap: wrap; align-items: center; justify-content: flex-end; gap: 6px; }
.review-row-decide { padding: 4px 12px; font-size: 12px; white-space: nowrap; }
.decide-title { display: block; margin-bottom: 6px; font-size: 13px; }
.review-panel { display: grid; gap: 10px; padding: 12px 0 4px; border-top: 1px solid var(--line); }
.review-panel > header { display: flex; align-items: baseline; justify-content: space-between; }
.review-panel > header b { font-size: 14px; }
.review-panel > header small { color: var(--muted); font-size: 12px; }
.review-hint { margin: 0; color: var(--muted); font-size: 12px; line-height: 1.8; }

.review-steps { display: grid; gap: 6px; margin: 0; padding: 0; list-style: none; }
.review-steps li {
  display: grid;
  grid-template-columns: 26px minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
  padding: 7px 9px;
  border: 1px solid var(--line);
  border-left: 3px solid #B9C8D8;
  background: var(--white);
}
.review-steps li.approved { border-left-color: var(--green); }
.review-steps li.rejected { border-left-color: var(--risk); }
.review-steps li.actionable { border-left-color: var(--blue); background: #EEF4FA; }
.review-steps li.excluded { opacity: .55; }
.review-step {
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--paper);
  font: 700 11px var(--display);
}
.review-body { display: grid; gap: 2px; min-width: 0; }
.review-body b { font-size: 13px; overflow-wrap: anywhere; }
.review-body b i {
  margin-left: 5px;
  padding: 0 4px;
  border: 1px solid var(--warning-graphic);
  color: var(--warning);
  font-size: 10px;
  font-style: normal;
}
.review-body b i.mine { border-color: var(--blue); color: var(--blue); }
.review-body small { color: var(--muted); font-size: 12px; }
.review-comment { margin: 2px 0 0; color: var(--ink); font-size: 12px; line-height: 1.6; white-space: pre-wrap; }

/* 历轮评审：默认是一条可点开的窄条，展开后才占地方 */
.review-rounds { margin: 10px 0 0; border: 1px solid var(--line); border-radius: 6px; background: var(--paper); }
.review-rounds > summary {
  padding: 7px 10px; font-size: 12px; color: var(--muted); cursor: pointer; list-style: revert;
}
.review-rounds > summary:hover { background: #F4F6F8; }
.review-rounds > summary b { color: var(--ink); }
.review-rounds > summary em { font-style: normal; color: var(--risk); }
.review-round { padding: 0 10px 8px 26px; }
.round-head { margin: 4px 0; font-size: 12px; font-weight: 600; color: var(--ink); }
.round-head small { margin-left: 6px; font-weight: 400; color: var(--muted); }
.round-list { margin: 0; padding: 0; list-style: none; display: grid; gap: 4px; }
.round-list li {
  padding: 5px 8px; border-left: 3px solid var(--line); background: var(--white);
  font-size: 12px; color: var(--muted);
}
.round-list li.rejected { border-left-color: var(--risk); }
.round-who { font-weight: 600; color: var(--ink); }
.round-verdict { margin-left: 6px; }
/* 「退回」要自己也认得出来，不能只靠左边那道红边——一眼扫过去看的是字 */
.round-list li.rejected .round-verdict { color: var(--risk); font-weight: 600; }
.round-when { margin-left: 6px; }
.round-comment { margin: 3px 0 0; color: var(--ink); line-height: 1.6; white-space: pre-wrap; }
.review-remove { min-height: 24px; display: inline-flex; align-items: center; padding: 0 8px; border: 1px solid var(--line-strong); border-radius: var(--radius-s); background: var(--white); cursor: pointer; font: inherit; font-size: 12px; font-weight: 700; line-height: 1; white-space: nowrap; color: var(--risk); border-color: rgba(196,63,56,.4); }
.review-remove:hover { border-color: var(--risk); background: rgba(196,63,56,.07); }
.review-remove:hover { color: var(--risk); }

.review-decide { display: grid; gap: 8px; }
.review-decide textarea { padding: 7px 9px; border: 1px solid var(--line); font: inherit; font-size: 12px; resize: vertical; }
.review-actions { display: flex; gap: 8px; }
.review-reject { border: 1px solid var(--risk); background: var(--white); color: var(--risk); }

.review-add { display: flex; gap: 8px; align-items: center; }
.review-add :deep(.person-picker) { flex: 1; min-height: 38px; padding: 5px 10px; border: 1px solid var(--line); border-radius: var(--radius-s); font: inherit; font-size: 12px; background: var(--white); }

.brief-box { display: grid; gap: 7px; padding: 9px 11px; border: 1px dashed #B9C8D8; background: var(--paper); }
.brief-head { display: flex; align-items: center; gap: 10px; }
.brief-head small { color: var(--muted); font-size: 12px; }
.brief-refresh { min-height: 24px; display: inline-flex; align-items: center; padding: 0 8px; border: 1px solid var(--line-strong); border-radius: var(--radius-s); background: var(--white); cursor: pointer; font: inherit; font-size: 12px; font-weight: 700; line-height: 1; white-space: nowrap; color: var(--blue); }
.brief-refresh:hover { border-color: var(--blue); background: rgba(0,117,174,.07); }
.brief-structural { margin: 0; font-size: 12px; }
.brief-structural b { color: var(--risk); }
.evidence-box { border: 1px solid var(--line); border-radius: 8px; padding: 10px 12px; margin-bottom: 10px; }
.evidence-box h4 { margin: 0 0 6px; font-size: 13px; }
.evidence-list { display: grid; gap: 6px; margin: 0; padding: 0; list-style: none; }
.evidence-list li { display: grid; gap: 1px; }
.evidence-list small { color: var(--muted); font-size: 12px; }
/* 能点的东西要看得出能点——下划线文字不算（0830 定的规矩） */
.evidence-link {
  display: inline-block; padding: 2px 8px; border: 1px solid var(--line);
  border-radius: 6px; font-size: 13px; color: var(--blue); text-decoration: none;
  justify-self: start; background: var(--surface);
}
.evidence-link:hover { border-color: var(--blue); }
.evidence-broken { font-size: 13px; color: var(--muted); }
/* 已取消那段压一档：看得见、点得到，但不和有效证据抢注意力 */
.evidence-voided-title { margin: 12px 0 6px; padding-top: 10px; border-top: 1px dashed var(--line); font-size: 13px; color: var(--muted); }
.evidence-voided-title em { font-style: normal; font-weight: 400; font-size: 12px; }
.evidence-list.is-voided .evidence-link { color: var(--muted); }
.evidence-warn { margin: 8px 0 0; color: #9a3412; background: #fff7ed; border: 1px solid #fed7aa; border-radius: 6px; padding: 8px 10px; font-size: 12px; line-height: 1.7; }
.brief-files { display: grid; gap: 2px; margin: 0; padding: 0 0 0 16px; font-size: 12px; }
.brief-files li.unreadable { color: var(--muted); }
.brief-files small { color: var(--muted); }
.brief-summary { margin: 0; padding: 7px 9px; background: var(--white); border-left: 3px solid var(--blue); font-size: 12px; line-height: 1.8; white-space: pre-wrap; }
.brief-checks { display: grid; gap: 3px; margin: 0; padding: 0; list-style: none; font-size: 12px; }
.brief-checks li { display: flex; gap: 6px; align-items: baseline; }
.brief-checks i { font-style: normal; font-weight: 800; }
.brief-checks li.ok i { color: var(--green); }
.brief-checks li.missing i { color: var(--risk); }
.brief-checks li.unclear i { color: var(--warning); }
.brief-vision { margin: 6px 0 0; padding: 6px 10px; border-left: 3px solid var(--amber, #b4761c); background: rgba(180,118,28,.07); color: var(--ink); font-size: 12.5px; line-height: 1.7; }
.brief-vision span { display: block; color: var(--muted); }

.brief-note { margin: 0; color: var(--muted); font-size: 12px; line-height: 1.7; }
</style>
